import { FC, useState } from 'react';
import { Button, ButtonProps, CircularProgress, Tooltip } from '@mui/material';
import UpdateIcon from '@mui/icons-material/Update';
import { useTriggerCronMutation } from '../../store/cron/cron.api';

interface RunCronButtonProps extends Omit<ButtonProps, 'onClick'> {
  tooltip?: string;
}

export const RunCronButton: FC<RunCronButtonProps> = ({
  tooltip = 'Runs updates for all posts to check expirations and statuses. This does not need to be clicked, this is scheduled to run every hour.',
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  startIcon = <UpdateIcon />,
  ...props
}) => {
  const [triggerCron] = useTriggerCronMutation();
  const [isRunning, setIsRunning] = useState(false);
  const [lastRun, setLastRun] = useState<Date | null>(null);

  const handleClick = async () => {
    try {
      setIsRunning(true);
      await triggerCron();
      setLastRun(new Date());
    } catch (error) {
      console.error('Failed to trigger cron job:', error);
    } finally {
      setIsRunning(false);
    }
  };

  const buttonText = lastRun 
    ? `Last run: ${lastRun.toLocaleTimeString()}`
    : 'Run Updates';

  return (
    <Tooltip title={tooltip}>
      <span>
        <Button
          variant={variant}
          color={color}
          size={size}
          startIcon={isRunning ? <CircularProgress size={20} color="inherit" /> : startIcon}
          onClick={handleClick}
          disabled={isRunning}
          sx={{ marginRight: 2 }}
          {...props}
        >
          {isRunning ? 'Running...' : buttonText}
        </Button>
      </span>
    </Tooltip>
  );
};
